/**
 * Profile Dropdown Component
 * Displays user profile menu with sign out option
 */

import { useEffect, useRef } from 'react';
import { User, LogOut, Settings, Download, Coins } from 'lucide-react';
import { PulsingDots } from '@/components/ui';
import styles from './ProfileDropdown.module.css';
import type { ProfileDropdownProps } from './ProfileDropdown.types';

export function ProfileDropdown({
  user,
  onSignOut,
  isOpen,
  onClose,
  onOpenSettings,
  canInstallPWA,
  onInstallPWA,
  credits,
  creditsLoading,
  onOpenCredits,
}: ProfileDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Close dropdown on Escape
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.dropdown} ref={dropdownRef} role="menu">
      <div className={styles.userInfo}>
        <div className={styles.avatar}>
          <User size={20} aria-hidden="true" />
        </div>
        <div className={styles.userDetails}>
          <span className={styles.userEmail}>{user.email}</span>
          <span className={styles.userLabel}>Account</span>
        </div>
      </div>

      <div className={styles.divider} />

      {onOpenCredits !== undefined && (
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            onClose();
            onOpenCredits?.();
          }}
          role="menuitem"
        >
          <Coins size={18} aria-hidden="true" />
          <span className={styles.creditsLabel}>Credits</span>
          <span className={styles.creditsBadge}>{creditsLoading ? <PulsingDots label="Loading credits" /> : (credits ?? 0)}</span>
        </button>
      )}

      <button
        type="button"
        className={styles.menuItem}
        onClick={() => {
          onClose();
          onOpenSettings?.();
        }}
        role="menuitem"
      >
        <Settings size={18} aria-hidden="true" />
        <span>Profile Settings</span>
      </button>

      {canInstallPWA && onInstallPWA && (
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            onInstallPWA();
            onClose();
          }}
          role="menuitem"
        >
          <Download size={18} aria-hidden="true" />
          <span>Install App</span>
        </button>
      )}

      <div className={styles.divider} />

      <button
        type="button"
        className={`${styles.menuItem} ${styles.menuItemDanger}`}
        onClick={() => {
          onSignOut();
          onClose();
        }}
        role="menuitem"
      >
        <LogOut size={18} aria-hidden="true" />
        <span>Sign Out</span>
      </button>
    </div>
  );
}
